// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/ManageStaff/ManageStaff.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../api';
import TenantAdminLayout from '../Layout/TenantAdminLayout';
import ConfirmationModal from './ConfirmationModal';
import AddStaffModal from './AddStaffModal';
import EditStaffModal from './EditStaffModal';
import BulkUploadModal from './BulkUploadModal';
import StaffGrid from './StaffGrid';
import StaffStats from './StaffStats';
import StaffFilters from './StaffFilters';
import StaffSearchBar from './StaffSearchBar';
import StaffBulkActions from './StaffBulkActions';
import StaffDetailsModal from './StaffDetailsModal';
import Pagination from './Pagination';
import { Users, UserPlus, Upload, AlertTriangle, CheckCircle, HelpCircle } from 'lucide-react';

const ManageStaff = () => {
  const location = useLocation();
  const tenantId = location.state?.tenantId;
  const userId = location.state?.userId;
  
  console.log('ManageStaff - tenantId:', tenantId);
  console.log('ManageStaff - userId:', userId);

  // Always fetch global stats for all staff (not paginated or filtered)
  const fetchStaffStats = useCallback(async () => {
    if (!tenantId) return;
    try {
      const response = await api.get(`/api/admin/staff/${tenantId}?status=all&limit=1`);
      if (response.data.stats) {
        setStats({
          total: response.data.stats.total,
          active: response.data.stats.active,
          inactive: response.data.stats.inactive,
          disabled: response.data.stats.disabled
        });
      }
    } catch (err) {
      console.error('Error fetching staff stats:', err);
    }
  }, [tenantId]);

  // State management
  const [, setStaff] = useState([]);
  const [paginatedStaff, setPaginatedStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prefix, setPrefix] = useState('');
  const [prefixSet, setPrefixSet] = useState(false);
  
  // Stats state
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    disabled: 0
  });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  // Selection states
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Form states
  const [newStaff, setNewStaff] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    role: 'Faculty',
  });
  const [editUser, setEditUser] = useState(null);
  const [selectedStaffDetails, setSelectedStaffDetails] = useState(null);
  
  // Filter states - UI filters (for display only)
  const [filters, setFilters] = useState({
    searchTerm: '',
    role: 'all',
    status: 'all',
    sortBy: 'name',
    sortOrder: 'asc'
  });

  // API filters (actual filters sent to backend)
  // Using a ref to track the latest API filters to avoid race conditions
  const apiFiltersRef = useRef({
    searchTerm: '',
    role: 'all',
    status: 'all',
    sortBy: 'name',
    sortOrder: 'asc'
  });
  
  // Flag to prevent multiple simultaneous fetches
  const isFetchingRef = useRef(false);
  const searchTimeoutRef = useRef(null);
  const initialFetchDoneRef = useRef(false);
  const filterTimeoutRef = useRef(null);
  
  // UI states
  const [isProcessing, setIsProcessing] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: false, message: '' });
  const [confirmModal, setConfirmModal] = useState({ 
    open: false, 
    title: '', 
    message: '', 
    onConfirm: null 
  });

  const normalizeRoleKey = useCallback((role) => {
    if (!role) return '';

    const normalized = String(role).trim().toLowerCase().replace(/[\s-]+/g, '_');
    if (normalized === 'controller_of_examination') return 'coe';
    return normalized;
  }, []);

  // Helper function to convert UI role to backend role
  const convertRoleToBackend = useCallback((role) => {
    if (!role || String(role).toLowerCase() === 'all') return 'all';
    return normalizeRoleKey(role);
  }, [normalizeRoleKey]);

  // Helper function to convert backend role to UI role
  const convertRoleToUI = useCallback((role) => {
    if (!role || String(role).toLowerCase() === 'all') return 'all';

    const normalized = normalizeRoleKey(role);
    if (normalized === 'coe') return 'Controller of Examination';
    if (normalized === 'coe_coordinator') return 'COE Coordinator';

    return normalized
      .split('_')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }, [normalizeRoleKey]);

  // Fetch tenant details
  const fetchTenantDetails = useCallback(async () => {
    if (!tenantId) return;
    
    try {
      const response = await api.get(`/api/admin/tenant/${tenantId}`);
      const tenantData = response.data.tenant;
      if (tenantData.staffPrefix) {
        setPrefix(tenantData.staffPrefix);
        setPrefixSet(true);
      }
    } catch (err) {
      console.error('Error fetching tenant details:', err);
    }
  }, [tenantId]);

  // Fetch staff with pagination from backend
  const fetchStaff = useCallback(async (page = 1, filtersToApply = null) => {
    if (!tenantId || isFetchingRef.current) return;
    
    // Use provided filters or current API filters ref
    const currentFilters = filtersToApply || apiFiltersRef.current;
    
    try {
      isFetchingRef.current = true;
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        page,
        limit: itemsPerPage,
        sortBy: currentFilters.sortBy,
        sortOrder: currentFilters.sortOrder
      });
      
      // Add role filter if not 'all'
      if (currentFilters.role && currentFilters.role !== 'all') {
        params.append('role', currentFilters.role);
      }
      
      // Add status filter if not 'all'
      if (currentFilters.status && currentFilters.status !== 'all') {
        params.append('status', currentFilters.status);
      }
      
      // Add search term if present
      if (currentFilters.searchTerm) {
        params.append('search', currentFilters.searchTerm);
      }

      console.log('Fetching staff with params:', params.toString());

      const response = await api.get(`/api/admin/staff/${tenantId}?${params}`);
      
      // Filter out tenant_admin users from the response
      const filteredStaff = response.data.staff.filter(member => 
        member.role?.toLowerCase() !== 'tenant_admin'
      ).map(member => ({
        ...member,
        role: convertRoleToUI(member.role)
      }));

      setStaff(filteredStaff);
      setTotalItems(response.data.pagination.total);
      setTotalPages(response.data.pagination.pages);
      setPaginatedStaff(filteredStaff);
      
      // Use backend-provided stats
      if (response.data.stats) {
        setStats({
          total: response.data.stats.total,
          active: response.data.stats.active,
          inactive: response.data.stats.inactive,
          disabled: response.data.stats.disabled
        });
      } else {
        // fallback for old API
        const activeCount = filteredStaff.filter(m => m.status === 'active').length;
        const inactiveCount = filteredStaff.filter(m => m.status === 'inactive').length;
        const disabledCount = filteredStaff.filter(m => m.status === 'disabled').length;
        setStats({
          total: response.data.pagination.total,
          active: activeCount,
          inactive: inactiveCount,
          disabled: disabledCount
        });
      }
      
      console.log('Staff fetched:', {
        count: filteredStaff.length,
        total: response.data.pagination.total,
        page: response.data.pagination.page,
        pages: response.data.pagination.pages
      });
      
      return filteredStaff;
    } catch (err) {
      console.error('Error fetching staff:', err);
      throw new Error('Failed to fetch staff');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [tenantId, itemsPerPage, convertRoleToUI]);

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    try {
      const response = await api.get('/api/roles');
      // Map 'coe' and 'Controller of Examination' and 'COE' to 'Controller of Examination' for display
      const filteredRoles = response.data.roles
        .filter(role => role.toLowerCase() !== 'tenant_admin')
        .map(role => convertRoleToUI(role));

      // Ensure COE Coordinator remains available even if role API is stale.
      filteredRoles.push('COE Coordinator');
      
      // Remove duplicates
      const uniqueRoles = [...new Set(filteredRoles)];
      setRoles(uniqueRoles);
      console.log('Roles fetched (tenant_admin excluded):', uniqueRoles);
    } catch (err) {
      console.error('Error fetching roles:', err);
      throw new Error('Failed to fetch roles');
    }
  }, [convertRoleToUI]);

  // Function to apply filters (debounced)
  const applyFilters = useCallback((newFilters) => {
    // Clear any existing timeout
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }
    
    // Set timeout to apply filters
    filterTimeoutRef.current = setTimeout(() => {
      // Convert role to backend format for API
      const backendFilters = {
        ...newFilters,
        role: convertRoleToBackend(newFilters.role)
      };
      
      // Update the API filters ref
      apiFiltersRef.current = backendFilters;
      
      // Fetch staff with new filters (reset to page 1)
      fetchStaff(1, backendFilters);
      setCurrentPage(1);
      fetchStaffStats();
      
      filterTimeoutRef.current = null;
    }, 300);
  }, [fetchStaff, fetchStaffStats, convertRoleToBackend]);

  // Handle filter change from StaffFilters component
  const handleFilterChange = useCallback((newFilters) => {
    // Update UI filters immediately
    setFilters(newFilters);
    
    // Apply filters with debounce
    applyFilters(newFilters);
  }, [applyFilters]);

  // Handle search input change
  const handleSearchChange = useCallback((value) => {
    // Update UI immediately
    setFilters(prev => ({ ...prev, searchTerm: value }));
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout to apply search after user stops typing
    searchTimeoutRef.current = setTimeout(() => {
      const newFilters = {
        ...filters,
        searchTerm: value
      };
      setFilters(newFilters);
      applyFilters(newFilters);
    }, 500);
  }, [filters, applyFilters]);

  // Fetch initial data only once
  useEffect(() => {
    if (!tenantId) {
      console.error('No tenantId provided');
      setLoading(false);
      return;
    }

    const loadInitialData = async () => {
      if (initialFetchDoneRef.current) return;
      try {
        setLoading(true);
        await fetchTenantDetails();
        await fetchRoles();
        
        // Initial API filters
        const initialBackendFilters = {
          searchTerm: '',
          role: 'all',
          status: 'all',
          sortBy: 'name',
          sortOrder: 'asc'
        };
        apiFiltersRef.current = initialBackendFilters;
        
        await fetchStaff(1, initialBackendFilters);
        await fetchStaffStats();
        initialFetchDoneRef.current = true;
      } catch (err) {
        console.error('Error fetching initial data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [tenantId, fetchTenantDetails, fetchRoles, fetchStaff, fetchStaffStats]);

  // Fetch staff when page changes
  useEffect(() => {
    if (!tenantId || !initialFetchDoneRef.current) return;
    fetchStaff(currentPage, apiFiltersRef.current);
  }, [currentPage, tenantId, fetchStaff]);

  // Handle successful staff addition
  const handleAddSuccess = (newStaffMember) => {
    fetchStaff(currentPage, apiFiltersRef.current);
    fetchStaffStats();
    setSelectedStaffIds([]);
    setSelectAll(false);
    setSubmitStatus({ 
      success: true, 
      message: `Staff member ${newStaffMember.firstName} ${newStaffMember.lastName || ''} added successfully!` 
    });
    setTimeout(() => {
      setSubmitStatus({ success: false, message: '' });
    }, 3000);
  };

  // Handle successful bulk upload
  const handleBulkSuccess = (results) => {
    fetchStaff(currentPage, apiFiltersRef.current).then(() => {
      fetchStaffStats();
      setSubmitStatus({ 
        success: true, 
        message: `Bulk upload completed: ${results.successful} successful, ${results.failed} failed` 
      });
      setTimeout(() => {
        setSubmitStatus({ success: false, message: '' });
      }, 5000);
    });
    setSelectedStaffIds([]);
    setSelectAll(false);
    setShowBulkUploadModal(false);
  };

  // Add staff function
  const handleAddStaff = async (e) => {
    e.preventDefault();
    
    if (!newStaff.role) {
      setSubmitStatus({ success: false, message: 'Please select a role' });
      return;
    }

    try {
      setIsProcessing(true);
      
      await api.post('/api/admin/add-user', {
        ...newStaff,
        role: convertRoleToBackend(newStaff.role),
        tenantId,
        createdBy: userId,
      });

      await fetchStaff(currentPage, apiFiltersRef.current);
      setShowAddModal(false);
      setNewStaff({ firstName: '', lastName: '', email: '', phoneNumber: '', role: 'faculty' });
      setSubmitStatus({ 
        success: true, 
        message: `Staff member ${newStaff.firstName} ${newStaff.lastName || ''} added successfully!` 
      });

    } catch (err) {
      console.error('Add staff error:', err);
      setSubmitStatus({ 
        success: false, 
        message: err.response?.data?.message || 'Error adding staff' 
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Edit staff function
  const handleEditStaff = async () => {
    if (!editUser.role) {
      setSubmitStatus({ success: false, message: 'Please select a role' });
      return;
    }

    try {
      setIsProcessing(true);
      
      // Clean phone number before sending
      const cleanPhone = editUser.phoneNumber?.replace(/\D/g, '') || '';
      
      console.log('Updating staff with data:', {
        userId: editUser.userId || editUser._id,
        firstName: editUser.firstName,
        lastName: editUser.lastName,
        email: editUser.email,
        phoneNumber: cleanPhone,
        role: convertRoleToBackend(editUser.role),
        tenantId
      });
      
      const response = await api.put('/api/admin/staff/update', {
        userId: editUser.userId || editUser._id,
        firstName: editUser.firstName,
        lastName: editUser.lastName,
        email: editUser.email,
        phoneNumber: cleanPhone,
        role: convertRoleToBackend(editUser.role),
        tenantId
      });

      console.log('Update response:', response.data);

      if (response.data.success) {
        await fetchStaff(currentPage, apiFiltersRef.current);
        await fetchStaffStats();
        setShowEditModal(false);
        setEditUser(null);
        setSubmitStatus({ 
          success: true, 
          message: 'Staff member updated successfully!' 
        });
      } else {
        setSubmitStatus({ 
          success: false, 
          message: response.data.message || 'Error updating staff' 
        });
      }

    } catch (error) {
      console.error('Edit staff error:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        setSubmitStatus({ 
          success: false, 
          message: error.response.data.message || 'Error updating staff' 
        });
      } else if (error.request) {
        console.error('Error request:', error.request);
        setSubmitStatus({ 
          success: false, 
          message: 'No response from server. Please check your connection.' 
        });
      } else {
        console.error('Error message:', error.message);
        setSubmitStatus({ 
          success: false, 
          message: 'An error occurred. Please try again.' 
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Delete staff function
  const handleDeleteStaff = async (userId) => {
    try {
      setIsProcessing(true);
      await api.delete(`/api/admin/delete-user/${userId}`, { 
        data: { tenantId } 
      });
      await fetchStaff(currentPage, apiFiltersRef.current);
      await fetchStaffStats();
      setSelectedStaffIds(prev => prev.filter(id => id !== userId));
      setSubmitStatus({ 
        success: true, 
        message: 'Staff member deleted successfully!' 
      });

    } catch (err) {
      console.error('Delete staff error:', err);
      setSubmitStatus({ 
        success: false, 
        message: err.response?.data?.message || 'Error deleting staff' 
      });
    } finally {
      setIsProcessing(false);
      setConfirmModal({ ...confirmModal, open: false });
    }
  };

  // Bulk delete function
  const handleBulkDelete = async () => {
    try {
      setIsProcessing(true);
      await Promise.all(
        selectedStaffIds.map(id => 
          api.delete(`/api/admin/delete-user/${id}`, { data: { tenantId } })
        )
      );
      await fetchStaff(currentPage, apiFiltersRef.current);
      await fetchStaffStats();
      setSelectedStaffIds([]);
      setSelectAll(false);
      setSubmitStatus({ 
        success: true, 
        message: `${selectedStaffIds.length} staff member(s) deleted successfully!` 
      });

    } catch (err) {
      console.error('Bulk delete error:', err);
      setSubmitStatus({ 
        success: false, 
        message: 'Error deleting selected staff' 
      });
    } finally {
      setIsProcessing(false);
      setConfirmModal({ ...confirmModal, open: false });
    }
  };

  // Status toggle function
  const handleStatusToggle = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      setIsProcessing(true);
      await api.patch('/api/admin/update-user-status', {
        userId,
        status: newStatus,
        tenantId,
      });
      await fetchStaff(currentPage, apiFiltersRef.current);
      await fetchStaffStats();
      setSubmitStatus({ 
        success: true, 
        message: `Staff member ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully!` 
      });
    } catch (err) {
      console.error('Status toggle error:', err);
      setSubmitStatus({ 
        success: false, 
        message: err.response?.data?.message || 'Error updating status' 
      });
    } finally {
      setIsProcessing(false);
      setConfirmModal({ ...confirmModal, open: false });
    }
  };

  // Quick action handlers
  const handleQuickEdit = (user) => {
    setEditUser(user);
    setShowEditModal(true);
  };

  const handleQuickStatusToggle = async (userId, currentStatus) => {
    await handleStatusToggle(userId, currentStatus);
  };

  const handleQuickDelete = (userId, userName) => {
    showDeleteConfirmation(userId, userName);
  };

  // View staff details
  const handleViewDetails = async (member) => {
    try {
      setIsProcessing(true);
      
      const memberId = member.userId || member._id;
      const response = await api.get(`/api/admin/user/${tenantId}/${memberId}`);
      
      setSelectedStaffDetails(response.data.user);
      setShowDetailsModal(true);
      
    } catch (err) {
      console.error('View details error:', err);
      setSubmitStatus({
        success: false,
        message: err.response?.data?.message || 'Error fetching staff details'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Selection handlers
  const handleSelectStaff = (id) => {
    setSelectedStaffIds(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedStaffIds([]);
    } else {
      setSelectedStaffIds(paginatedStaff.map(member => member.userId || member._id));
    }
    setSelectAll(!selectAll);
  };

  // Pagination handler
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setSelectedStaffIds([]);
    setSelectAll(false);
  };

  // Modal close handlers
  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setNewStaff({ firstName: '', lastName: '', email: '', phoneNumber: '', role: 'faculty' });
    setSubmitStatus({ success: null, message: '', showPassword: false });
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditUser(null);
  };

  const handleCloseBulkModal = () => {
    setShowBulkUploadModal(false);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedStaffDetails(null);
  };

  // Confirmation modal handlers
  const showDeleteConfirmation = (userId, userName) => {
    setConfirmModal({
      open: true,
      title: 'Delete Staff Member',
      message: `Are you sure you want to delete ${userName}? This action cannot be undone.`,
      onConfirm: () => handleDeleteStaff(userId)
    });
  };

  const showBulkDeleteConfirmation = () => {
    if (selectedStaffIds.length === 0) return;
    
    setConfirmModal({
      open: true,
      title: 'Delete Selected Staff',
      message: `Are you sure you want to delete ${selectedStaffIds.length} staff member(s)? This action cannot be undone.`,
      onConfirm: handleBulkDelete
    });
  };

  const showStatusToggleConfirmation = (userId, userName, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? 'enable' : 'disable';
    
    setConfirmModal({
      open: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Staff Member`,
      message: `Are you sure you want to ${action} ${userName}?`,
      onConfirm: () => handleStatusToggle(userId, currentStatus)
    });
  };

  // Clear status message
  useEffect(() => {
    if (submitStatus.message) {
      const timer = setTimeout(() => {
        setSubmitStatus({ success: false, message: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [submitStatus.message]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, []);

  // Loading Screen Wrapper
  if (loading && !initialFetchDoneRef.current) {
    return (
      <TenantAdminLayout>
        <div className="h-[50vh] flex flex-col items-center justify-center gap-3">
          <div className="animate-spin rounded-xl h-7 w-7 border-2 border-indigo-600 border-t-transparent" />
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Loading Staff Records...</p>
        </div>
      </TenantAdminLayout>
    );
  }

  return (
    <TenantAdminLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Main Workspace Header Panel */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                  <Users size={18} />
                </div>
                <h1 className="text-lg md:text-xl font-bold text-slate-900 tracking-tight">Staff Management</h1>
              </div>
              <p className="text-xs text-slate-500 font-medium">
                Manage accounts • Showing <span className="text-slate-800 font-bold">{paginatedStaff.length}</span> of <span className="text-slate-800 font-bold">{totalItems}</span> members
                {filters.searchTerm && ` (Filtered search for: "${filters.searchTerm}")`}
              </p>
            </div>
            
            {/* Primary Action Button Bar */}
            <div className="flex items-center gap-2.5 shrink-0">
              <button
                onClick={() => prefixSet && setShowBulkUploadModal(true)}
                className={`h-9 px-3.5 text-xs font-bold rounded-xl flex items-center gap-1.5 transition-all border ${
                  prefixSet 
                    ? 'bg-white hover:bg-slate-50 border-slate-200 text-slate-700 shadow-xs active:scale-95' 
                    : 'bg-slate-100 text-slate-400 border-transparent cursor-not-allowed'
                }`}
                disabled={!prefixSet}
                title={!prefixSet ? 'Please set a staff prefix rule first' : ''}
              >
                <Upload size={14} />
                <span>Bulk Upload</span>
              </button>
              
              <button
                onClick={() => prefixSet && setShowAddModal(true)}
                className={`h-9 px-3.5 text-xs font-bold rounded-xl flex items-center gap-1.5 transition-all shadow-xs ${
                  prefixSet 
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white active:scale-95' 
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
                disabled={!prefixSet}
                title={!prefixSet ? 'Please set a staff prefix rule first' : ''}
              >
                <UserPlus size={14} />
                <span>Add Staff</span>
              </button>
            </div>
          </div>

          {/* Setup Action Warning Banner */}
          {!prefixSet && (
            <div className="mt-4 flex items-start gap-2.5 bg-amber-50/80 border border-amber-200 p-3 rounded-xl text-amber-800">
              <AlertTriangle size={15} className="shrink-0 mt-0.5 text-amber-600" />
              <div className="text-xs font-medium">
                <span className="font-bold">Setup Required:</span> Please configure an uppercase tracking code prefix in the parameter widget below to open profile adding and management tools.
              </div>
            </div>
          )}
        </div>

        {/* Global Action Banner System */}
        {submitStatus.message && (
          <div className={`p-3.5 rounded-xl border text-xs font-semibold flex items-center gap-2 animate-fadeIn ${
            submitStatus.success 
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200' 
              : 'bg-rose-50 text-rose-800 border-rose-200'
          }`}>
            {submitStatus.success ? <CheckCircle size={14} className="text-emerald-600" /> : <AlertTriangle size={14} className="text-rose-600" />}
            <p>{submitStatus.message}</p>
          </div>
        )}

        {/* Institutional Statistics Module Component */}
        <StaffStats
          staffCount={stats.total}
          activeCount={stats.active}
          inactiveCount={stats.inactive}
          disabledCount={stats.disabled}
          prefix={prefix}
          prefixSet={prefixSet}
          onPrefixChange={(value) => {
            if (/^[A-Z]{0,4}$/.test(value)) {
              setPrefix(value);
            }
          }}
          onSetPrefix={async () => {
            try {
              setIsProcessing(true);
              await api.post('/api/admin/set-prefix', { tenantId, prefix });
              setPrefixSet(true);
              setSubmitStatus({ success: true, message: 'Prefix set successfully!' });
            } catch (err) {
              setSubmitStatus({ 
                success: false, 
                message: err.response?.data?.message || 'Error setting prefix' 
              });
            } finally {
              setIsProcessing(false);
            }
          }}
          isProcessing={isProcessing}
        />

        {/* Filters and Searching Toolbar Box */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <StaffSearchBar
              searchTerm={filters.searchTerm}
              onSearchChange={handleSearchChange}
              tenantId={tenantId}
              onQuickEdit={handleQuickEdit}
              onQuickStatusToggle={handleQuickStatusToggle}
              onQuickDelete={handleQuickDelete}
              onViewDetails={handleViewDetails}
            />
          </div>
          <div className="shrink-0 border-t lg:border-t-0 lg:border-l border-slate-100 pt-3 lg:pt-0 lg:pl-4">
            <StaffFilters
              filters={filters}
              onFiltersChange={handleFilterChange}
              roles={roles}
            />
          </div>
        </div>

        {/* Row Selection Multi-Action Segment */}
        {selectedStaffIds.length > 0 && (
          <StaffBulkActions
            selectedCount={selectedStaffIds.length}
            onClear={() => {
              setSelectedStaffIds([]);
              setSelectAll(false);
            }}
            onDelete={showBulkDeleteConfirmation}
          />
        )}

        {/* Master Database Grid Layer */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
          <StaffGrid
            staff={paginatedStaff}
            selectedStaffIds={selectedStaffIds}
            selectAll={selectAll}
            onSelectStaff={handleSelectStaff}
            onSelectAll={handleSelectAll}
            onEdit={(user) => {
              setEditUser(user);
              setShowEditModal(true);
            }}
            onStatusToggle={(userId, userName, currentStatus) => 
              showStatusToggleConfirmation(userId, userName, currentStatus)
            }
            onDelete={(userId, userName) => showDeleteConfirmation(userId, userName)}
            onViewDetails={handleViewDetails}
          />

          {/* Embedded Grid Bottom Pagination Footer */}
          {totalPages > 1 && (
            <div className="bg-slate-50 border-t border-slate-200/60 px-4 py-3">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
              />
            </div>
          )}
        </div>

        {/* Table Blank State Exception Handling View */}
        {paginatedStaff.length === 0 && !loading && (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center max-w-md mx-auto">
            <HelpCircle size={32} className="mx-auto text-slate-400 mb-3" />
            <h3 className="text-sm font-bold text-slate-900 mb-1">No staff records found</h3>
            <p className="text-xs text-slate-500 max-w-xs mx-auto mb-5">
              {filters.searchTerm || filters.role !== 'all' || filters.status !== 'all' 
                ? 'Try clearing active criteria configurations or altering your filter text terms.'
                : 'Get started by typing and enrolling your first institution faculty profile.'}
            </p>
            <button
              onClick={() => prefixSet && setShowAddModal(true)}
              className={`inline-flex items-center h-9 px-4 text-xs font-bold rounded-xl transition-all ${
                prefixSet 
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-xs' 
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
              disabled={!prefixSet}
              title={!prefixSet ? 'Please set a staff prefix rule first' : ''}
            >
              Add Staff Member
            </button>
          </div>
        )}

        {/* Render Layer Popups & Overlays */}
        {showAddModal && (
          <AddStaffModal
            open={showAddModal}
            onClose={handleCloseAddModal}
            onSubmit={handleAddStaff}
            newStaff={newStaff}
            setNewStaff={setNewStaff}
            roles={roles}
            isProcessing={isProcessing}
            submitStatus={submitStatus}
            setSubmitStatus={setSubmitStatus}
            tenantId={tenantId}
            userId={userId}
            api={api}
            setStaff={setStaff}
            setShowAddModal={setShowAddModal}
            onSuccess={handleAddSuccess}
          />
        )}

        {showEditModal && (
          <EditStaffModal
            open={showEditModal}
            editUser={editUser}
            onClose={handleCloseEditModal}
            onChange={setEditUser}
            onSave={handleEditStaff}
            isProcessing={isProcessing}
            roles={roles}
            tenantId={tenantId}
          />
        )}

        {showBulkUploadModal && (
          <BulkUploadModal
            open={showBulkUploadModal}
            onClose={handleCloseBulkModal}
            onSuccess={handleBulkSuccess}
            api={api}
            tenantId={tenantId}
            roles={roles}
          />
        )}

        {showDetailsModal && (
          <StaffDetailsModal
            open={showDetailsModal}
            staffMember={selectedStaffDetails}
            onClose={handleCloseDetailsModal}
            onStatusToggle={(userId, currentStatus) => 
              showStatusToggleConfirmation(userId, selectedStaffDetails?.firstName, currentStatus)
            }
          />
        )}

        {confirmModal.open && (
          <ConfirmationModal
            open={confirmModal.open}
            title={confirmModal.title}
            message={confirmModal.message}
            onConfirm={confirmModal.onConfirm}
            onCancel={() => setConfirmModal({ ...confirmModal, open: false })}
            isProcessing={isProcessing}
          />
        )}
      </div>
    </TenantAdminLayout>
  );
};

export default ManageStaff;